import { Instruction } from "../instruction/Instruction.model";
import { Prerequisite } from "../prerequisite/Prerequisite.model";
import { TopicReleasePolicy } from "../topic-release-policy/TopicReleasePolicy.model";
import {
  ReleaseConversation,
  ReleaseConversationFeedback,
} from "../release-conversation/ReleaseConversation.model";
import { GlobalInstruction } from "../global-instruction/GlobalInstruction.model";

// Re-export the feedback type for backward compatibility
export type { ReleaseConversationFeedback };

export enum ReleaseStatus {
  draft = "draft",
  published = "published",
}

export enum EscalationStrategy {
  NONE = "NONE",
  CREATE_TICKET = "CREATE_TICKET",
  LIVE_AGENT = "LIVE_AGENT",
}

export interface TopicReleaseVisualization {
  id: number;
  topicReleasePolicyId: number;
  generated: boolean;
  visualization: string[];
}

export type ReleaseLoginEnforcement = {
  enabled?: boolean;
  maxAttempts?: number;
  closingCondition?: string;
  supportCondition?: string;
  brief?: string;
};

export interface Release {
  id: number;
  releasePolicy: any;
  filterPolicy?: any;
  tenantId: string;
  status: ReleaseStatus;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string;
  simulatorId: number;
  customToneOfVoice?: string;
  escalationStrategy: EscalationStrategy;
  maxBackAndForth: number;
  lengthOfConversation: string;
  includeOpening: boolean;
  includeLink: boolean;
  strictMode: boolean;
  hideSourceUrls: boolean;
  articleLinksOnRequest?: boolean;
  toneOfVoice: string;
  openingLine: string;
  closingLine: string;
  knowledgeBrandId?: number;
  topicReleasePolicies?: Array<
    TopicReleasePolicy & {
      instructions: Instruction[];
      prerequisites: Prerequisite[];
      topicReleasePolicyVisualizations?: TopicReleaseVisualization[];
    }
  >;
  releaseConversations?: ReleaseConversation[];
  globalInstructions?: GlobalInstruction[];
  loginEnforcement?: ReleaseLoginEnforcement;
  version?: number;
}

export type Version = Release & {
  version: number;
};

export type UpsertReleaseConversationRequest = {
  releaseId: number;
  comment?: string;
  releaseConversationId?: number;
  topicId?: number;
  simulatorId?: number;
  isAgent?: boolean;
  feedback?: ReleaseConversationFeedback;
  feedbackComment?: string;
};

export type UpsertReleaseConversationResponse = ReleaseConversation | null;

export type UpsertReleaseLoginEnforcementDto = ReleaseLoginEnforcement;

export type UpsertReleaseParamsDto = {
  id?: number;
  simulatorId: number;
  status?: ReleaseStatus;
  releasePolicy?: any;
  filterPolicy?: any;
  customToneOfVoice?: string;
  escalationStrategy?: string;
  maxBackAndForth?: number;
  lengthOfConversation?: string;
  includeOpening?: boolean;
  includeLink?: boolean;
  strictMode?: boolean;
  hideSourceUrls?: boolean;
  articleLinksOnRequest?: boolean;
  toneOfVoice?: string;
  openingLine?: string;
  closingLine?: string;
  knowledgeBrandId?: number;
  loginEnforcement?: UpsertReleaseLoginEnforcementDto;
};

function sortReleaseFunction(release1: Release, release2: Release): number {
  return (
    new Date(release1.createdAt).getTime() -
    new Date(release2.createdAt).getTime()
  );
}

function enhanceReleaseWithVersion(release: Release, index: number): Version {
  return {
    ...release,
    version: index + 1,
  };
}

export function getReleasesWithVersions(releases: Release[]): Version[] {
  return releases.sort(sortReleaseFunction).map(enhanceReleaseWithVersion);
}
